import { isArc } from './arcWalls.jsx';

// شکستنِ دیوار در محلِ برخورد: وقتی سرِ یک دیوار به وسطِ دیوارِ دیگری می‌رسد (اتصالِ T) یا دو دیوار از روی هم رد
// می‌شوند (اتصالِ +)، دیوارِ برخوردشده از همان نقطه به دو دیوارِ جدا تبدیل می‌شود — هر تکه جدا انتخاب و پوشش‌دهی
// می‌شود. در/پنجره/بازشوهای روی آن دیوار، روی تکه‌ای که مرکزشان در آن است می‌مانند (جای واقعی‌شان عوض نمی‌شود).

const TOL = 5; // سانتی‌متر — سرِ دیواری که این‌قدر به بدنه‌ی دیوارِ دیگر نزدیک است «برخورد» حساب می‌شود
const MIN_PIECE = 5; // تکه‌ی کوتاه‌تر از این ساخته نمی‌شود (برخورد نزدیکِ سر/ته = اتصالِ گوشه، نه شکستن)

const len = (w) => Math.hypot(w.x2 - w.x1, w.y2 - w.y1);

function project(px, py, w) {
  const dx = w.x2 - w.x1;
  const dy = w.y2 - w.y1;
  const l2 = dx * dx + dy * dy;
  if (l2 === 0) return null;
  const t = ((px - w.x1) * dx + (py - w.y1) * dy) / l2;
  const qx = w.x1 + dx * t;
  const qy = w.y1 + dy * t;
  return { t, x: qx, y: qy, d: Math.hypot(px - qx, py - qy) };
}

const interior = (t, L) => t * L >= MIN_PIECE && (1 - t) * L >= MIN_PIECE;

// تقاطعِ دو پاره‌خط (فقط وقتی هر دو در «داخلِ» خودشان باشند) → { ta, tb } یا null
function crossing(a, b) {
  const rx = a.x2 - a.x1;
  const ry = a.y2 - a.y1;
  const sx = b.x2 - b.x1;
  const sy = b.y2 - b.y1;
  const den = rx * sy - ry * sx;
  if (Math.abs(den) < 1e-9) return null; // موازی
  const qx = b.x1 - a.x1;
  const qy = b.y1 - a.y1;
  const ta = (qx * sy - qy * sx) / den;
  const tb = (qx * ry - qy * rx) / den;
  if (!interior(ta, len(a)) || !interior(tb, len(b))) return null;
  return { ta, tb };
}

// plan: { walls, doors, windows, openings }. changedIds: دیوارهایی که همین الان کشیده/جابه‌جا شدند.
// newId(): شناسه‌ی دیوارِ جدید. → { walls, doors, windows, openings, splitIds: Set(دیوارهای شکسته‌شده) }
export function applyJunctionSplits(plan, changedIds, newId) {
  const changed = new Set(changedIds);
  let walls = plan.walls.map((w) => ({ ...w }));

  // ۱) سرِ دیوارِ تغییرکرده که خیلی نزدیکِ بدنه‌ی دیوارِ دیگری است، دقیقاً روی آن می‌نشیند
  walls.forEach((a) => {
    if (!changed.has(a.id)) return;
    [
      ['x1', 'y1'],
      ['x2', 'y2'],
    ].forEach(([kx, ky]) => {
      let best = null;
      walls.forEach((b) => {
        if (b.id === a.id || isArc(b)) return; // بدنه‌ی قوس با خطِ راست سنجیده نمی‌شود
        const p = project(a[kx], a[ky], b);
        if (p && p.d <= TOL && interior(p.t, len(b)) && (!best || p.d < best.d)) best = p;
      });
      if (best && best.d > 0.01) {
        a[kx] = Math.round(best.x * 100) / 100;
        a[ky] = Math.round(best.y * 100) / 100;
      }
    });
  });

  // ۲) محل‌های شکستن: wallId → [t]
  const cuts = new Map();
  const addCut = (w, t) => {
    if (!cuts.has(w.id)) cuts.set(w.id, []);
    cuts.get(w.id).push(t);
  };
  walls.forEach((a) => {
    if (!changed.has(a.id)) return;
    walls.forEach((b) => {
      if (b.id === a.id) return;
      // سرِ a روی بدنه‌ی b → b می‌شکند (دیوارِ منحنی شکسته نمی‌شود)
      if (!isArc(b))
      [
        [a.x1, a.y1],
        [a.x2, a.y2],
      ].forEach(([px, py]) => {
        const p = project(px, py, b);
        if (p && p.d <= 0.5 && interior(p.t, len(b))) addCut(b, p.t);
      });
      // سرِ b روی بدنه‌ی a (دیوارِ جابه‌جاشده روی سرِ دیوارِ دیگری نشست) → a می‌شکند
      if (!isArc(a))
      [
        [b.x1, b.y1],
        [b.x2, b.y2],
      ].forEach(([px, py]) => {
        const p = project(px, py, a);
        if (p && p.d <= 0.5 && interior(p.t, len(a))) addCut(a, p.t);
      });
      // ردشدن از روی هم → هر دو می‌شکنند
      if (!isArc(a) && !isArc(b) && !(changed.has(b.id) && b.id < a.id)) {
        const c = crossing(a, b);
        if (c) {
          addCut(a, c.ta);
          addCut(b, c.tb);
        }
      }
    });
  });

  if (!cuts.size) {
    return { walls, doors: plan.doors, windows: plan.windows, openings: plan.openings, splitIds: new Set() };
  }

  // ۳) شکستن + جابه‌جاییِ در/پنجره/بازشو روی تکه‌ی درست
  const pieceMap = new Map(); // wallId → [{ id, from, to }] (به سانتی‌متر روی دیوارِ اصلی)
  const out = [];
  walls.forEach((w) => {
    const ts = cuts.get(w.id);
    if (!ts) {
      out.push(w);
      return;
    }
    const L = len(w);
    const sorted = [...new Set(ts.map((t) => Math.round(t * L * 10) / 10))].sort((p, q) => p - q);
    const stops = [];
    sorted.forEach((d) => {
      if (d >= MIN_PIECE && L - d >= MIN_PIECE && (!stops.length || d - stops[stops.length - 1] >= MIN_PIECE)) stops.push(d);
    });
    if (!stops.length) {
      out.push(w);
      return;
    }
    const ux = (w.x2 - w.x1) / L;
    const uy = (w.y2 - w.y1) / L;
    const at = (d) => ({ x: Math.round((w.x1 + ux * d) * 100) / 100, y: Math.round((w.y1 + uy * d) * 100) / 100 });
    const bounds = [0, ...stops, L];
    const pieces = [];
    for (let k = 0; k < bounds.length - 1; k += 1) {
      const p0 = k === 0 ? { x: w.x1, y: w.y1 } : at(bounds[k]);
      const p1 = k === bounds.length - 2 ? { x: w.x2, y: w.y2 } : at(bounds[k + 1]);
      const id = k === 0 ? w.id : newId();
      out.push({ ...w, id, x1: p0.x, y1: p0.y, x2: p1.x, y2: p1.y });
      pieces.push({ id, from: bounds[k], to: bounds[k + 1] });
    }
    pieceMap.set(w.id, { L, pieces });
  });
  walls = out;

  const moveItems = (items) =>
    (items || []).map((it) => {
      const pm = pieceMap.get(it.wallId);
      if (!pm) return it;
      const d = it.ratio * pm.L;
      const piece = pm.pieces.find((p) => d <= p.to) || pm.pieces[pm.pieces.length - 1];
      const pl = piece.to - piece.from;
      return { ...it, wallId: piece.id, ratio: pl > 0 ? Math.max(0, Math.min(1, (d - piece.from) / pl)) : 0.5 };
    });

  return {
    walls,
    doors: moveItems(plan.doors),
    windows: moveItems(plan.windows),
    openings: moveItems(plan.openings),
    splitIds: new Set(pieceMap.keys()),
  };
}
